/**
 * Empty-cart variant preview switcher.
 * Force any of the 5 empty-cart drawer variants via URL flag, only for the
 * page load where the flag is present — no persistence across page loads:
 *   ?emptyv1  ?emptyv2  ?emptyv3  ?emptyv4  ?emptyv5
 *
 * When shown, the active variant is reflected on <html> as:
 *   <html data-empty-cart="v1">
 * A plain page load (no flag) never adds this attribute.
 *
 * A/B tool (e.g. Convert Experiences) setup — put ONE of these in the
 * variation's custom JS:
 *   window.AB_EMPTYCART = 'v1';
 *   window.AB_EMPTYCART = 'v2';
 *   window.AB_EMPTYCART = 'v3';
 *   window.AB_EMPTYCART = 'reset';   // original / default empty cart
 * AB_EMPTYCART is a live property: it works whether Convert sets it before
 * this script loads or any time after the page has loaded — the variant is
 * applied immediately on assignment. A URL flag always wins over it.
 * If Convert instead sets html[data-empty-cart] before init() runs, that
 * is still respected.
 *
 * Cart-open handling: the real mini-cart drawer (#knfufnw.cart_content)
 * toggles a "fadeInd" class open/closed (see cart-custom-drawer.css). The
 * close icon's onclick="animatefade()" only hides our panel visually — it
 * does NOT clear which variant is assigned. A MutationObserver watches the
 * drawer and re-shows the assigned variant every time it's reopened, so
 * closing and reopening the cart keeps showing the same v1..v5 instead of
 * reverting to nothing / the default markup.
 */
(function () {
  'use strict';

  var VARIANTS = [
    { param: 'emptyv1', id: 'v1', selector: '.emptycart_vone' },
    { param: 'emptyv2', id: 'v2', selector: '.emptycart_vtwo' },
    { param: 'emptyv3', id: 'v3', selector: '.emptycart_vthree' },
  ];

  var PREVIEW_CLASS = 'empty-cart-forced-preview';
  var HTML_ATTR = 'data-empty-cart';
  var DRAWER_OPEN_CLASS = 'fadeInd';

  var activeVariant = null;
  var initialized = false;
  var forcedByUrl = null;
  // Convert may have set this before the script loaded — keep that value.
  var abEmptyCartValue = window.AB_EMPTYCART;

  function getForcedVariant(params) {
    for (var i = 0; i < VARIANTS.length; i++) {
      if (params.has(VARIANTS[i].param)) return VARIANTS[i];
    }
    return null;
  }

  function findVariant(id) {
    return VARIANTS.filter(function (v) { return v.id === id; })[0] || null;
  }

  // Hides every panel and clears the assignment entirely (no variant active).
  function hideAll() {
    activeVariant = null;
    document.querySelectorAll('.js-empty-cart__drawer').forEach(function (drawer) {
      drawer.style.display = 'none';
      drawer.classList.remove(PREVIEW_CLASS);
    });
    document.body.classList.remove(PREVIEW_CLASS);
    document.documentElement.removeAttribute(HTML_ATTR);
  }

  // Hides the visible panel but keeps the assignment — used for the close
  // icon, so reopening the cart can bring the same variant straight back.
  function closePanel() {
    document.querySelectorAll('.js-empty-cart__drawer').forEach(function (drawer) {
      drawer.style.display = 'none';
      drawer.classList.remove(PREVIEW_CLASS);
    });
    document.body.classList.remove(PREVIEW_CLASS);
  }

  function showVariant(variant) {
    var target = document.querySelector(variant.selector);

    if (!target) {
      console.warn(
        '[empty-cart-preview] "' + variant.param + '" requested but that variant\'s block ' +
        'was not rendered — check that its "Enable" setting is turned on in theme settings.'
      );
      return false;
    }

    document.querySelectorAll('.js-empty-cart__drawer').forEach(function (drawer) {
      if (drawer !== target) {
        drawer.style.display = 'none';
        drawer.classList.remove(PREVIEW_CLASS);
      }
    });

    // target.style.display = 'block';
    target.classList.add(PREVIEW_CLASS);
    document.body.classList.add(PREVIEW_CLASS);
    document.documentElement.setAttribute(HTML_ATTR, variant.id);
    activeVariant = variant;
    return true;
  }

  // Empty-cart v3: tabbed product carousel, built on Swiper (already loaded
  // site-wide — see initUpsellSwiper in theme.liquid for the same pattern
  // used by the cart-drawer upsell slider). Each tab panel gets its own
  // Swiper instance, created lazily the first time its tab is shown. The
  // prev/next buttons scroll the tab bar itself (for when tabs overflow);
  // the product carousel is swipe/drag only (Swiper freeMode + grabCursor),
  // no button control.
  function initV3Swipers() {
    if (typeof Swiper === 'undefined') return;

    document.querySelectorAll('.emptycart_vthree .ec3_wrap').forEach(function (wrap) {
      if (wrap.ec3Initialized) return;
      wrap.ec3Initialized = true;

      var tabs = wrap.querySelectorAll('.ec3_tab');
      var panels = wrap.querySelectorAll('.ec3_panel');
      var tabsContainer = wrap.querySelector('.ec3_tabs');
      var prevBtn = wrap.querySelector('.ec3_nav_prev');
      var nextBtn = wrap.querySelector('.ec3_nav_next');
      var activeSwiper = null;

      // function updateTabNavState() {
      //   if (!tabsContainer) return;
      //   var maxScroll = tabsContainer.scrollWidth - tabsContainer.clientWidth;
      //   if (prevBtn) prevBtn.disabled = tabsContainer.scrollLeft <= 0;
      //   if (nextBtn) nextBtn.disabled = tabsContainer.scrollLeft >= maxScroll - 1;
      // }

      function updateTabNavState() {
        var activeIndex = getActiveTabIndex();
        if (prevBtn) prevBtn.disabled = activeIndex <= 0;
        if (nextBtn) nextBtn.disabled = activeIndex >= tabs.length - 1;
      }

      function getOrCreateSwiper(panel) {
        var sliderEl = panel.querySelector('.ec3_products');
        if (!sliderEl) return null;

        if (sliderEl.ec3Swiper) return sliderEl.ec3Swiper;

        var instance = new Swiper(sliderEl, {
          slidesPerView: 'auto',
          spaceBetween: 12,
          freeMode: true,
          grabCursor: true,
          watchOverflow: true,
          observer: true,
          observeParents: true,
        });

        sliderEl.ec3Swiper = instance;
        return instance;
      }

      function activatePanel(panel) {
        activeSwiper = getOrCreateSwiper(panel);
        if (activeSwiper) activeSwiper.update();
      }

      tabs.forEach(function (tab) {
        tab.addEventListener('click', function () {
          var target = tab.getAttribute('data-ec3-tab');

          tabs.forEach(function (t) {
            var isActive = t === tab;
            t.classList.toggle('active', isActive);
            t.setAttribute('aria-selected', isActive ? 'true' : 'false');
          });

          var nextPanel = null;
          panels.forEach(function (p) {
            var isActive = p.getAttribute('data-ec3-panel') === target;
            p.classList.toggle('active', isActive);
            if (isActive) nextPanel = p;
          });

          if (nextPanel) activatePanel(nextPanel);
        });
      });

      // if (tabsContainer) {
      //   tabsContainer.addEventListener('scroll', updateTabNavState);
      // }
      // if (prevBtn) {
      //   prevBtn.addEventListener('click', function () {
      //     if (tabsContainer) tabsContainer.scrollBy({ left: -120, behavior: 'smooth' });
      //   });
      // }
      // if (nextBtn) {
      //   nextBtn.addEventListener('click', function () {
      //     if (tabsContainer) tabsContainer.scrollBy({ left: 120, behavior: 'smooth' });
      //   });
      // }

      function getActiveTabIndex() {
        var activeIndex = 0;
        tabs.forEach(function (t, i) {
          if (t.classList.contains('active')) activeIndex = i;
        });
        return activeIndex;
      }

      function activateTab(index) {
        if (index < 0 || index >= tabs.length) return;
        var tab = tabs[index];
        var target = tab.getAttribute('data-ec3-tab');

        tabs.forEach(function (t, i) {
          var isActive = i === index;
          t.classList.toggle('active', isActive);
          t.setAttribute('aria-selected', isActive ? 'true' : 'false');
        });

        var nextPanel = null;
        panels.forEach(function (p) {
          var isActive = p.getAttribute('data-ec3-panel') === target;
          p.classList.toggle('active', isActive);
          if (isActive) nextPanel = p;
        });

        if (nextPanel) activatePanel(nextPanel);

        // Scroll the tab into view in the tab bar
        if (tabsContainer) tab.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });

        updateTabNavState();
      }

      if (prevBtn) {
        prevBtn.addEventListener('click', function () {
          activateTab(getActiveTabIndex() - 1);
        });
      }
      if (nextBtn) {
        nextBtn.addEventListener('click', function () {
          activateTab(getActiveTabIndex() + 1);
        });
      }

      updateTabNavState();
      window.addEventListener('resize', updateTabNavState);

      var initialPanel = wrap.querySelector('.ec3_panel.active') || panels[0];
      if (initialPanel) activatePanel(initialPanel);
    });
  }

  // The cart drawer's content loads asynchronously, so the v3 markup may
  // not exist yet at DOMContentLoaded. Mirror the site's own
  // initUpsellSwiper() approach (theme.liquid): try immediately, retry on
  // the drawer's own "cart:open" event, and fall back to a MutationObserver
  // in case that event isn't dispatched on every code path.
  function watchForV3Swipers() {
    initV3Swipers();

    document.addEventListener('cart:open', function () {
      setTimeout(initV3Swipers, 300);
    });

    var observer = new MutationObserver(function () {
      if (document.querySelector('.emptycart_vthree .ec3_wrap')) {
        initV3Swipers();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
  }

  function watchCartDrawer() {
    var drawer = document.getElementById('knfufnw') || document.querySelector('.cart_content.cart__drawer');
    if (!drawer) return;

    var observer = new MutationObserver(function () {
      if (drawer.classList.contains(DRAWER_OPEN_CLASS) && activeVariant) {
        showVariant(activeVariant);
      }
    });

    observer.observe(drawer, { attributes: true, attributeFilter: ['class'] });
  }

  // 'reset' -> default empty cart; 'v1' / 'v2' / 'v3' -> that variant.
  function applyAbValue(value) {
    if (value === 'reset') {
      hideAll();
      return true;
    }
    var variant = findVariant(value);
    if (variant) {
      showVariant(variant);
      return true;
    }
    console.warn(
      '[empty-cart-preview] Unknown AB_EMPTYCART value "' + value + '" — use \'v1\', \'v2\', \'v3\' or \'reset\'.'
    );
    return false;
  }

  // Turn window.AB_EMPTYCART into a live property so an assignment made
  // after init() (e.g. Convert running late) still switches the variant.
  try {
    Object.defineProperty(window, 'AB_EMPTYCART', {
      configurable: true,
      enumerable: true,
      get: function () { return abEmptyCartValue; },
      set: function (value) {
        abEmptyCartValue = value;
        if (initialized && !forcedByUrl) applyAbValue(value);
      }
    });
  } catch (e) {
    // Property locked by someone else — init() still reads the value once.
  }

  function init() {
    var params = new URLSearchParams(window.location.search);
    forcedByUrl = getForcedVariant(params);
    var abValue = window.AB_EMPTYCART;

    if (forcedByUrl) {
      // 1. URL flag (?emptyv1, ?emptyv2, ?emptyv3) wins
      showVariant(forcedByUrl);
    } else if (abValue && applyAbValue(abValue)) {
      // 2. window.AB_EMPTYCART = 'v1' / 'v2' / 'v3' / 'reset'
    } else {
      // 3. No AB_EMPTYCART: respect attribute set by Convert, else default
      var existingId = document.documentElement.getAttribute(HTML_ATTR);
      var existing = existingId ? findVariant(existingId) : null;

      if (existing) {
        showVariant(existing);
      } else {
        hideAll();
      }
    }

    initialized = true;
    watchCartDrawer();
    watchForV3Swipers();
  }

  // Close icon in ab-empty-cart.liquid calls onclick="animatefade()" — was
  // never defined. Visual close only; keeps the variant assigned.
  window.animatefade = window.animatefade || function () {
    closePanel();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();